/**
 * Decide when an unbatch card can stop. The waiting list only shows lines
 * the queue rebuilds. A line already on another batch, or on an order that
 * will not re-enter production, never increments that count.
 */

const tagsOf = (order) =>
  new Set((Array.isArray(order?.statusTags) ? order.statusTags : []).map(String));

/**
 * "pending" means the queue row may still appear. Anything else will not
 * come back, so the card should stop on this check.
 */
export const lineHoldReason = ({ orderItem, order, batchId, sellerOnHold }) => {
  const thisBatch = String(batchId || "");
  const lineBatch = orderItem?.batchId ? String(orderItem.batchId) : "";
  if (orderItem?.batched === true && lineBatch && lineBatch === thisBatch) {
    return "pending";
  }
  if (orderItem?.batched === true && lineBatch && lineBatch !== thisBatch) {
    return "rebatched";
  }
  if (orderItem?.routed === true) return "routed";
  if (!orderItem && !order) return "missing";

  const tags = tagsOf(order);
  if (order?.cancelled === true || tags.has("cancelled")) return "cancelled";
  if (
    order?.shipped === true ||
    tags.has("shipped") ||
    tags.has("partially-shipped") ||
    tags.has("ready-for-pickup")
  ) {
    return "shipped";
  }
  if (order?.routed === true || tags.has("routed")) return "routed";
  if (tags.has("on-hold") || sellerOnHold === true) return "on-hold";
  return "pending";
};

const reasonOf = (lines) => {
  const unique = Array.from(new Set(lines.map((line) => line.reason)));
  return unique.length === 1 ? unique[0] : "mixed";
};

/**
 * back/total come from the waiting list. lines are the batch's order lines.
 * The card is done when every piece is back, or settled when none of the
 * remaining lines can return.
 */
export const settleUnbatch = ({ back, total, lines }) => {
  const safeBack = Number(back) || 0;
  const safeTotal = Number(total) || 0;
  if (safeTotal > 0 && safeBack >= safeTotal) {
    return { phase: "done", back: safeBack, total: safeTotal, held: 0, reason: "" };
  }
  const list = lines || [];
  if (!list.length || list.some((line) => line.reason === "pending")) {
    return { phase: "checking", back: safeBack, total: safeTotal, held: 0, reason: "" };
  }
  return {
    phase: "settled",
    back: safeBack,
    total: safeTotal,
    held: Math.max(0, safeTotal - safeBack),
    reason: reasonOf(list),
  };
};

const noun = (count, unit) => {
  const word = unit === "orders" ? "order" : "item";
  const n = Number(count) || 0;
  return `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;
};

const WHY = {
  rebatched: "the order is already on another batch",
  shipped: "the order is shipped",
  cancelled: "the order is cancelled",
  routed: "the order was routed",
  "on-hold": "the order is on hold",
  missing: "the line is no longer on the order",
  mixed: "the order is shipped, cancelled, routed, or already on another batch",
};

export const settledMessage = ({ back, total, unit, reason }) => {
  const left = Math.max(0, Number(total) - Number(back));
  const why = WHY[reason] || "it will not return to the waiting list";
  if (Number(back) > 0) {
    return `${noun(back, unit)} of ${noun(total, unit)} are back. ${noun(left, unit)} stayed off because ${why}.`;
  }
  return `Unbatch finished. ${noun(left, unit)} stayed off the waiting list because ${why}.`;
};
